import { isJust, isNothing, type Maybe } from "./maybe";
import {
  entityDisplayName,
  findOccurrenceNames,
} from "../editor/entityOccurrences";
import { expandPossessiveNameForms } from "./nameForms";
import type { EntityFrontmatter } from "./entityFrontmatter";
import { joinProjectPath } from "./runtime";
import { listProjectDatabaseEntries } from "./tauri/database";
import { readProjectFile, writeFile } from "./tauri/core";
import { readZwConfig, writeZwConfig } from "./tauri/project";
import { PROJECT_DATABASE_DIR, type ZwConfig } from "./zwConfig";

const LEGACY_DICTIONARY_FILE = ".zwdictionary";

type DictionaryListener = (words: string[]) => void;
const dictionaryListeners = new Set<DictionaryListener>();

/** Keep in-memory projectConfig.dictionary in sync after disk writes. */
export function onProjectDictionaryChange(
  listener: DictionaryListener,
): () => void {
  dictionaryListeners.add(listener);
  return () => dictionaryListeners.delete(listener);
}

function notifyDictionaryChange(words: string[]): void {
  for (const listener of dictionaryListeners) listener(words);
}

/** Split an entity name / phrase into Harper dictionary tokens. */
export function tokenizeForDictionary(text: string): string[] {
  const matches = text.match(/[\p{L}\p{N}']+/gu);
  if (matches === null) return [];
  return matches
    .map((token) => token.replace(/^'+|'+$/g, ""))
    .filter((token) => token.length >= 2);
}

export function uniqueDictionaryWords(words: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const word of words) {
    const trimmed = word.trim();
    if (trimmed.length === 0) continue;
    const key = trimmed.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function parseDictionaryJson(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed) === false) return [];
    return uniqueDictionaryWords(
      parsed.filter((item): item is string => typeof item === "string"),
    );
  } catch {
    return [];
  }
}

async function readLegacyDictionaryFile(rootPath: string): Promise<string[]> {
  try {
    const raw = await readProjectFile(
      joinProjectPath(rootPath, LEGACY_DICTIONARY_FILE),
    );
    return parseDictionaryJson(raw);
  } catch {
    return [];
  }
}

async function clearLegacyDictionaryFile(rootPath: string): Promise<void> {
  try {
    await writeFile(joinProjectPath(rootPath, LEGACY_DICTIONARY_FILE), "[]\n");
  } catch {
    /* ignore */
  }
}

export async function loadUserDictionary(
  rootPath: Maybe<string>,
): Promise<string[]> {
  if (isNothing(rootPath) || rootPath.value.length === 0) return [];

  const config = await readZwConfig(rootPath.value);
  let words = uniqueDictionaryWords(config.dictionary ?? []);
  const legacy = await readLegacyDictionaryFile(rootPath.value);
  if (legacy.length > 0) {
    words = uniqueDictionaryWords([...words, ...legacy]);
    await saveUserDictionary(rootPath, words);
    await clearLegacyDictionaryFile(rootPath.value);
  }
  return words;
}

export async function saveUserDictionary(
  rootPath: Maybe<string>,
  words: string[],
): Promise<string[]> {
  const next = uniqueDictionaryWords(words);
  if (isNothing(rootPath) || rootPath.value.length === 0) return next;
  const config = await readZwConfig(rootPath.value);
  const updated: ZwConfig = { ...config, dictionary: next };
  await writeZwConfig(rootPath.value, updated);
  notifyDictionaryChange(next);
  return next;
}

export async function addUserDictionaryWord(
  rootPath: Maybe<string>,
  word: string,
): Promise<string[]> {
  const existing = await loadUserDictionary(rootPath);
  return saveUserDictionary(rootPath, [...existing, word]);
}

/**
 * Display names for every Database entry (H1, else file stem) plus their
 * `aka` aliases (#72), via `findOccurrenceNames`.
 * `live` overrides the on-disk body when that entry is open in the editor.
 */
export async function collectDatabaseDisplayNames(
  rootPath: string,
  live: Maybe<{
    fileName: string;
    doc: string;
    fields?: EntityFrontmatter;
  }>,
): Promise<string[]> {
  const entries = await listProjectDatabaseEntries(rootPath);
  const names = await Promise.all(
    entries.map(async ({ name: fileName, fields: diskFields }) => {
      const liveM = live;
      const fields =
        isJust(liveM) && liveM.value.fileName === fileName
          ? (liveM.value.fields ?? diskFields)
          : diskFields;
      let doc: string;
      if (isJust(liveM) && liveM.value.fileName === fileName) {
        doc = liveM.value.doc;
      } else {
        try {
          doc = await readProjectFile(
            joinProjectPath(rootPath, `${PROJECT_DATABASE_DIR}/${fileName}`),
          );
        } catch {
          doc = "";
        }
      }
      const displayName = entityDisplayName(doc, fileName);
      return findOccurrenceNames({
        name: displayName,
        aka: fields.aka,
        plural: fields.plural,
        adjective: fields.adjective,
      });
    }),
  );
  return uniqueDictionaryWords(names.flat());
}

/** Flatten entity display names into dictionary tokens for Harper. */
export function databaseWordsFromNames(names: string[]): string[] {
  const tokens = uniqueDictionaryWords(names.flatMap(tokenizeForDictionary));
  return uniqueDictionaryWords(expandPossessiveNameForms(tokens));
}
